import type { RequestRow } from "./requestLogGridTypes";

const COUNT_EMPTY_DISPLAY = "—";
const LATENCY_EMPTY_DISPLAY = "-";

/** Below this duration, show milliseconds. */
const LATENCY_MS_THRESHOLD_MS = 1000;
/** Below this duration, show seconds; otherwise minutes. */
const LATENCY_SECONDS_THRESHOLD_MS = 60_000;

const MILLISECONDS_PER_SECOND = 1000;
const MILLISECONDS_PER_MINUTE = 60_000;

const LATENCY_DISPLAY_DECIMAL_PLACES = 2;

export const formatCountCell = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined) {
    return COUNT_EMPTY_DISPLAY;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === "string") {
    const n = Number.parseInt(value, 10);
    return Number.isFinite(n) ? String(n) : COUNT_EMPTY_DISPLAY;
  }
  return COUNT_EMPTY_DISPLAY;
};

const toNumberOrNull = (value: number | string | null | undefined): number | null => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const getLatencyFromRow = (row?: RequestRow): number | null => {
  if (!row?.request_start_time || !row?.request_end_time) {
    return null;
  }

  const startMs = new Date(row.request_start_time).getTime();
  const endMs = new Date(row.request_end_time).getTime();

  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
    return null;
  }

  return Math.max(0, endMs - startMs);
};

export const formatLatency = (
  value: number | string | null | undefined,
  row?: RequestRow,
): string => {
  const latency = toNumberOrNull(value) ?? getLatencyFromRow(row);
  if (latency === null) {
    return LATENCY_EMPTY_DISPLAY;
  }

  if (latency < LATENCY_MS_THRESHOLD_MS) {
    return `${latency.toFixed(LATENCY_DISPLAY_DECIMAL_PLACES)} ms`;
  }

  if (latency < LATENCY_SECONDS_THRESHOLD_MS) {
    return `${(latency / MILLISECONDS_PER_SECOND).toFixed(LATENCY_DISPLAY_DECIMAL_PLACES)} s`;
  }

  return `${(latency / MILLISECONDS_PER_MINUTE).toFixed(LATENCY_DISPLAY_DECIMAL_PLACES)} min`;
};
